import { LIMIT } from "@/lib/constants";
import { gql, useQuery, useMutation } from "@apollo/client";

export const GET_MY_NOTIFICATIONS = gql`
  query MyNotifications($limit: Int!, $cursor: String) {
    myNotifications(limit: $limit, cursor: $cursor) {
      notifications {
        id
        type
        message
        createdAt
        metadata
        projectId
        recipients {
          read
        }
      }
      totalCount
      unreadCount
      nextCursor
    }
  }
`;

export const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($notificationId: String!) {
    markNotificationAsRead(notificationId: $notificationId) {
      id
      recipients {
        id
        read
        readAt
      }
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_AS_READ = gql`
  mutation MarkAllNotificationsAsRead {
    markAllNotificationsAsRead
  }
`;

export const useMyNotifications = () => {
  const { data, loading, error, fetchMore, refetch } = useQuery(
    GET_MY_NOTIFICATIONS,
    {
      variables: { limit: LIMIT },
      notifyOnNetworkStatusChange: true,
    }
  );

  const loadMore = () => {
    if (!data?.myNotifications?.nextCursor) return;

    fetchMore({
      variables: {
        cursor: data.myNotifications.nextCursor,
        limit: LIMIT,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          myNotifications: {
            ...fetchMoreResult.myNotifications,
            notifications: [
              ...prev.myNotifications.notifications,
              ...fetchMoreResult.myNotifications.notifications,
            ],
          },
        };
      },
    });
  };

  return {
    notifications: data?.myNotifications?.notifications || [],
    unreadCount: data?.myNotifications?.unreadCount ?? 0,
    totalCount: data?.myNotifications?.totalCount ?? 0,
    hasMore: !!data?.myNotifications?.nextCursor,
    loading,
    error,
    loadMore,
    refetchNotifications: refetch,
  };
};

export const useMarkNotificationAsRead = () => {
  const [markAsRead, { loading, error }] =
    useMutation(MARK_NOTIFICATION_AS_READ);

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead({
      variables: { notificationId },
    });
  };

  return { handleMarkAsRead, loading, error };
};

export const useMarkAllNotificationsAsRead = () => {
  const [markAll, { loading, error }] =
    useMutation(MARK_ALL_NOTIFICATIONS_AS_READ);

  const handleMarkAllAsRead = async () => {
    await markAll();
  };

  return { handleMarkAllAsRead, loading, error };
};

export const SAVE_PUSH_SUBSCRIPTION = gql`
  mutation SavePushSubscription($subscription: String!) {
    savePushSubscription(subscription: $subscription)
  }
`;

export const useSavePushSubscription = () => {
  const [saveSub, { loading, error }] = useMutation(SAVE_PUSH_SUBSCRIPTION);
  const handleSaveSubscription = async (subscription: PushSubscription) => {
    await saveSub({
      variables: { subscription: JSON.stringify(subscription) },
    });
  };
  return { handleSaveSubscription, loading, error };
};
